/* ==========================================================================
   Segal — gallery metal-image fix
   --------------------------------------------------------------------------
   THE BUG (reported by Yaniv 2026-09-01, reproduced on gold-name-necklace-loanne):
   generate a name in 14K Yellow Gold, then switch the Metal Type to 14K White
   Gold, and the customer sees an EMPTY gallery. The product data is correct -
   the white-gold media exists and the variant is linked to it - so this is
   purely a storefront rendering failure.

   WHAT ACTUALLY HAPPENS. The theme lazy-loads every gallery slide except the
   first: each ships a 1x1 GIF as `src` with the real file in `data-master`.
   Measured on the broken slide:

       class     = "rimage__img preloaded entered loaded lazyload"
       src       = "data:image/gif;base64,R0lGODlhAQABAIAAAP..."   <- placeholder
       srcset    = ".../7722253418630-14k-white-gold_608x...jpg"
       opacity   = 0.001
       data-ll-status = "loaded"

   So the lazy-loader marked the image "loaded" and set `srcset`, but never
   promoted `src` off the placeholder and never added the `donothide` class the
   theme uses to fade an image in. A working slide looks like:

       class = "rimage__img donothide"     src = the real file     opacity = 1

   WHY THE OBVIOUS FIXES DO NOT WORK. Measured, in order:
     - adding `donothide`            -> opacity stayed 0.001
     - setting a real `src`          -> opacity stayed 0.001
     - `style.opacity = 1 !important` -> opacity STILL 0.001. The element carries
       a running `transition: opacity .3s`, and CSS transitions sit above even
       !important author declarations in the cascade, so the inline rule loses.
     - the rule itself is unreachable: it lives in one of the 9 cross-origin
       stylesheets on this page, so it cannot be found or overridden by selector.

   THE FIX. Replace the element. A fresh <img> has no lazy-loader state, no
   `data-ll-status`, and no running transition to fight, so it simply renders.
   The replacement is built to look exactly like a slide the theme loaded itself.

   SCOPE AND SAFETY:
     - only `img.rimage__img` inside gallery slides and thumbnails
     - only images that are ACTUALLY broken (placeholder src or ~zero opacity)
       AND carry a usable `data-master`; a healthy image is never touched
     - never `.product-gallery__sale-ribbon` (overwriting the SALE ribbon with
       the necklace photo was a real bug on 2026-08-28 - do not reintroduce it)
     - only the active slide and the thumbnails, so we do not eagerly pull all
       12 images on every page view
   Delete the one <script> line in the snippet to remove this entirely.
   ========================================================================== */
(() => {
  'use strict';

  const TICK = 250;

  const MAIN_SEL  = '.product-gallery__main_item.slick-active img.rimage__img, ' +
                    '.product-gallery__main_item.slick-current img.rimage__img';
  const THUMB_SEL = '.product-gallery__thumbnail_item img.rimage__img';

  const M = window.SegalNameNecklaceMetrics = window.SegalNameNecklaceMetrics || {};
  M.galleryImagesRepaired = 0;

  const isPlaceholder = (img) => String(img.getAttribute('src') || '').slice(0, 5) === 'data:';

  const looksBroken = (img) => {
    if (isPlaceholder(img)) return true;
    // A slide that never got `donothide` sits at 0.001 even with a real src.
    const o = parseFloat(getComputedStyle(img).opacity);
    return !isNaN(o) && o < 0.9;
  };

  const repair = (img) => {
    // The sale ribbon is not a product photo and must never be rewritten.
    if (img.closest('.product-gallery__sale-ribbon')) return false;

    if (!looksBroken(img)) return false;

    // TWO SHAPES OF BROKEN IMAGE, found 2026-09-01.
    //  (a) Generated products: `src` is still the 1x1 placeholder and the real file is
    //      in `data-master` as a {width} template. Rebuild src/srcset from it.
    //  (b) Legacy catalogue products (e.g. gold-name-necklace-charlotte, created 2019):
    //      `data-master` is EMPTY, the real image has already loaded (naturalWidth
    //      1024), and the ONLY thing wrong is that it sits at opacity 0.001 because the
    //      fade class never landed. Here there is nothing to rebuild - cloning the node
    //      away from its stuck transition is the whole fix, so keep its own src/srcset.
    // The first cut required a usable data-master and so silently skipped (b), leaving
    // a blank gallery on metal switch across the existing 638 name products.
    const master = img.getAttribute('data-master') || '';
    const canRebuild = master.indexOf('{width}') !== -1;
    const hasRealSrc = !isPlaceholder(img) && img.naturalWidth > 1;
    if (!canRebuild && !hasRealSrc) return false;

    // CLONE, do not rebuild. The first cut hand-picked a few attributes onto a new
    // <img> and dropped data-aspect-ratio-original, data-scale and itemprop - which is
    // what the theme sizes the box from. Reported by Yaniv 2026-09-01: "i see that
    // pictures are stretched". Measured: a square 1024x1024 file laid out at 68x87
    // (ratio 0.781 against a natural 1.000) with object-fit:fill, so it was genuinely
    // squashed, not letterboxed. cloneNode(false) carries EVERY attribute across; we
    // then change only what has to change.
    const fresh = img.cloneNode(false);

    // KEEP THE THEME'S OWN CLASSES. Setting className outright to
    // 'rimage__img donothide' destroyed the `rimage__img--c` modifier, which is what
    // gives a thumbnail `object-fit: cover`. Without it the image computes to
    // `object-fit: fill` and a square photo is squashed into the 68x87 thumbnail slot.
    // Reported by Yaniv 2026-09-01: "i see that pictures are stretched". Measured
    // against the live theme: live thumbs are `rimage__img rimage__img--c` / cover,
    // mine were `rimage__img donothide` / fill, same 68x87 box.
    // So: drop only the lazy-loader's own classes, keep everything else, add donothide.
    const LAZY_CLASSES = ['lazyload', 'lazyloading', 'lazyloaded', 'preloaded', 'entered', 'loaded'];
    LAZY_CLASSES.forEach((c) => fresh.classList.remove(c));
    fresh.classList.add('donothide');
    // Lazy-loader state must not come along, or we inherit the very thing we are fixing.
    fresh.removeAttribute('data-ll-status');
    fresh.removeAttribute('onload');
    fresh.setAttribute('loading', 'eager');
    if (canRebuild) {
      fresh.setAttribute('srcset', master.replace('{width}', '1090') + ' 1x');
      fresh.setAttribute('src', master.replace('{width}', '1024'));
    }
    // else: the clone keeps the element's own src/srcset, which are already correct.

    img.replaceWith(fresh);
    M.galleryImagesRepaired += 1;
    M.lastGalleryRepair = {
      file: (master || String(img.getAttribute('src') || '')).split('/').pop(),
      mode: canRebuild ? 'rebuilt-from-master' : 'unstuck-in-place',
      at: new Date().toISOString()
    };
    return true;
  };

  setInterval(() => {
    try {
      document.querySelectorAll(MAIN_SEL).forEach(repair);
      document.querySelectorAll(THUMB_SEL).forEach(repair);
    } catch (_) {
      // Never let a display fix break the page it is fixing.
    }
  }, TICK);
})();
